import type { WorkflowCommittee } from "../api/workflow";
import type { AssignedReviewer } from "../types/ess";

// Sub-role token → display label. Backend stores the token in
// metadata.assigned_reviewers; display surfaces use the label. Single
// source of truth, and the fallback for entries assigned before the
// workflow config carried named slots.
const SUB_ROLE_LABELS: Record<AssignedReviewer["sub_role"], string> = {
  chairman: "Chairman",
  convener: "Convener",
  facilitator: "Facilitator",
  member: "Member",
};

// "WC" / "SC" — the uppercased committee token used in role labels and
// chips.
function committeeShort(committee: AssignedReviewer["committee"]): string {
  return committee.toUpperCase();
}

// "WC Chairman" / "SC Facilitator" — the committee + sub-role label shown
// on assigned-reviewer rows. Takes just the two fields so both a full
// AssignedReviewer and the picker's ReviewerOption can be passed.
export function reviewerRoleLabel(
  r: Pick<AssignedReviewer, "committee" | "sub_role">,
): string {
  return `${committeeShort(r.committee)} ${SUB_ROLE_LABELS[r.sub_role]}`;
}

// The seat a reviewer fills, as the workflow config names it —
// "Convener", "Member 2", … Falls back to the committee + sub-role label
// for entries assigned before slots existed, or when the config has no
// such slot any more.
export function reviewerSlotLabel(
  r: Pick<AssignedReviewer, "committee" | "sub_role" | "slot">,
  committees: Record<string, WorkflowCommittee> | undefined,
): string {
  const slot = r.slot
    ? committees?.[r.committee]?.slots.find((s) => s.key === r.slot)
    : undefined;
  return slot?.label ?? reviewerRoleLabel(r);
}

// Two-letter initials for a reviewer avatar. First letter of the first
// two whitespace-separated tokens, uppercased. Falls back to "?" for
// empty / null names.
export function reviewerInitials(name: string | null | undefined): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}
